import {FC, useState} from 'react';
import {useCan} from 'src/permissions/hooks';
import {BadgeOutlined, Edit, PhotoCameraOutlined} from '@mui/icons-material';
import {Box, Button, FormControlLabel, Stack, Switch, Typography} from '@mui/material';
import {FormattedMessage, useIntl} from 'react-intl';
import {companyApi, usersApi} from 'src/api';
import {useAppDispatch, useAppSelector, useDefineIsRealEstateRole} from 'src/app/hooks';
import {AppDispatch} from 'src/app/store';
import AMPModal from 'src/components/common/AMPModal';
import {DropZoneModal} from 'src/components/common/DropZoneModal';
import {UserForm} from 'src/components/forms/UserForm';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {EditUserForm} from '../../forms/EditUserForm';
import {CancelButton} from '../../common/CancelButton';
import {SubmitButton} from '../../common/SubmitButton';
import {ErrorIcon} from '../../icons/ErrorIcon';
import {UserDetailsType} from './CompanyDetails.types';
import {UserIdentityCard} from '../UserIdentityCard';
import {UserFact} from '../UserIdentityCard/UserIdentityCard.types';
import {actionButtonSx} from 'src/components/common/actionButton.sx';
import {softSwitchSx} from 'src/components/common/switch.sx';
import {companySettingsSx, deleteModalContentSx} from './CompanyDetails.sx';
import {isErrorObject} from 'src/utils/helper';
import {NotifyError} from 'src/utils/notification';
import {UserResetWrapper} from '../UserResetWrapper';
import {EditCompanyForm} from 'src/components/forms/EditCompanyForm';

type AddUserAvatarMutationTrigger = ReturnType<typeof usersApi.useSetUserAvatarMutation>[0];

const handleTrigger = async (
    file: File[],
    userId: number,
    trigger: AddUserAvatarMutationTrigger,
    dispatch: AppDispatch,
    handleCloseImageModal: () => void,
    handleBlockSubmit: (status: boolean) => void
) => {
    try {
        handleBlockSubmit(true);
        dispatch(preloaderChangeStatus(true));
        const formData = new FormData();
        formData.append('file', file[0]);
        await trigger({data: formData, userId}).unwrap();
        handleCloseImageModal();
    } catch (error) {
        if (isErrorObject(error)) {
            NotifyError(error.data.message);
        }
    } finally {
        dispatch(preloaderChangeStatus(false));
        handleBlockSubmit(false);
    }
};

export const CompanyDetails: FC<UserDetailsType> = ({data}) => {
    const canUpdateAvatar = useCan('profile.updateAvatar');
    const canEditProfile = useCan('profile.update');
    const canEditName = useCan('users.updateName');
    const intl = useIntl();
    const {authInfo} = useAppSelector((state) => state.common);

    const [showEditModal, setShowEditModal] = useState(false);
    const [showEditNameModal, setShowEditNameModal] = useState(false);
    const [open, setOpen] = useState<boolean>(false);
    const [openDropZone, setOpenDropZone] = useState<boolean>(false);
    const [userImage, setUserImage] = useState<File[]>();
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const canReadResetPassword = useCan('users.update');
    const canUpdateCompany = useCan('companies.update');

    const dialogTitle = intl.formatMessage({id: 'editUser'});
    const uploadDialogTitle = intl.formatMessage({id: 'addNewImage'});

    const dispatch = useAppDispatch();
    const [trigger] = usersApi.useSetUserAvatarMutation();
    const [setConsumptionBilling, {isLoading: isSwitchingConsumption}] =
        companyApi.useSetCompanyConsumptionBillingMutation();
    const [setChargingEmails, {isLoading: isSwitchingChargingEmails}] =
        companyApi.useSetCompanyChargingEmailNotificationsMutation();

    // The same query arguments the edit form passes, so both share one cache entry and this card re-reads
    // the company after the switch. The company API's own tags never reach a users API query.
    const {refetch} = usersApi.useGetUserByIdQuery({
        businessId: data?.business?.id ?? 0,
        userId: data.id,
    });

    const consumptionBilled = data?.company?.chargeConsumption !== false;
    // Opt-in on the server (V1.190.0), so an absent value is off - the opposite default to the switch
    // above it, and the reason this one is not written as `!== false`.
    const chargingEmailsSent = data?.company?.chargingEmailNotifications === true;

    const handleToggleConsumption = async () => {
        const companyId = data?.company?.id;

        if (!companyId) {
            return;
        }

        try {
            await setConsumptionBilling({
                companyId,
                businessId: data?.business?.id ?? 0,
                chargeConsumption: !consumptionBilled,
            }).unwrap();
            refetch();
        } catch (error) {
            if (isErrorObject(error)) {
                NotifyError(error.data.message);
            }
        }
    };

    const handleToggleChargingEmails = async () => {
        const companyId = data?.company?.id;

        if (!companyId) {
            return;
        }

        try {
            await setChargingEmails({
                companyId,
                businessId: data?.business?.id ?? 0,
                chargingEmailNotifications: !chargingEmailsSent,
            }).unwrap();
            refetch();
        } catch (error) {
            if (isErrorObject(error)) {
                NotifyError(error.data.message);
            }
        }
    };

    const handleShowEditModal = (status: boolean) => () => setShowEditModal(status);
    const handleShowEditNameModal = (status: boolean) => () => setShowEditNameModal(status);
    const [blockSubmit, setBlockSubmit] = useState(false);

    const handleBlockSubmit = (status: boolean) => {
        setBlockSubmit(status);
    };

    const handleCloseModal = () => {
        setOpen(false);
    };

    const handleCloseImageModal = () => {
        setOpenDropZone(false);
    };

    const handleSetImage = (file: File[] | null) => {
        file && setUserImage(file);
    };

    const handleSubmit = () => {
        userImage &&
        handleTrigger(
            userImage,
            data.id,
            trigger,
            dispatch,
            handleCloseImageModal,
            handleBlockSubmit
        );
    };

    const handleShowDeleteModal = (status: boolean) => {
        return () => {
            setShowDeleteModal(status);
        };
    };

    const facts: UserFact[] = [
        {label: 'companyName', value: data?.company?.name},
        {label: 'loginEmail', value: data.emailAddress},
        {
            label: 'billingAddress',
            value: data?.company?.billingAddress && [
                [data.company.billingAddress.street, data.company.billingAddress.houseNumber].filter(Boolean).join(' '),
                [data.company.billingAddress.zipCode, data.company.billingAddress.city].filter(Boolean).join(' '),
                data.company.billingAddress.country,
            ].filter(Boolean).join(', '),
            note: data?.company?.billingEmail,
        },
        {
            label: 'contactPerson',
            value: data?.company?.contactPerson
                && `${data.company.contactPerson.firstName ?? ''} ${data.company.contactPerson.lastName ?? ''}`.trim(),
            note: data?.company?.contactPerson?.phone,
        },
    ];

    return (
        <>
            <UserIdentityCard
                data={data}
                title={data?.company?.name || data.emailAddress || ''}
                typeLabel={String(data.userType)}
                facts={facts}
                actions={(
                    <>
                        {canUpdateAvatar && (
                            <Button
                                variant="contained"
                                sx={actionButtonSx('32px', 'secondary')}
                                startIcon={<PhotoCameraOutlined/>}
                                onClick={() => setOpenDropZone(true)}
                            >
                                <FormattedMessage id="photo"/>
                            </Button>
                        )}
                        {canEditName && (
                            <Button
                                variant="contained"
                                sx={actionButtonSx('32px', 'secondary')}
                                startIcon={<BadgeOutlined/>}
                                onClick={handleShowEditNameModal(true)}
                            >
                                <FormattedMessage id="edit"/>
                            </Button>
                        )}
                        {canEditProfile && (
                            <Button
                                variant="contained"
                                sx={actionButtonSx('32px', 'secondary')}
                                startIcon={<Edit/>}
                                onClick={handleShowEditModal(true)}
                            >
                                <FormattedMessage id="edit"/>
                            </Button>
                        )}
                        {(canReadResetPassword || data.id === authInfo.id) && (
                            <UserResetWrapper
                                data={data}
                                toolbar
                            />
                        )}
                    </>
                )}
                footer={canUpdateCompany && !!data?.company?.id && (
                    <Stack
                        direction={{xs: 'column', sm: 'row'}}
                        spacing={2}
                        sx={companySettingsSx.columns}
                    >
                        <Box sx={companySettingsSx.column}>
                            <Typography
                                variant="subtitle1"
                                color="textPrimary"
                            >
                                <FormattedMessage id="invoiceSettings"/>
                            </Typography>

                            <FormControlLabel
                                label={intl.formatMessage({id: 'chargeConsumption'})}
                                control={(
                                    <Switch
                                        sx={softSwitchSx}
                                        checked={consumptionBilled}
                                        disabled={isSwitchingConsumption}
                                        onChange={handleToggleConsumption}
                                    />
                                )}
                            />
                        </Box>

                        <Box sx={companySettingsSx.column}>
                            <Typography
                                variant="subtitle1"
                                color="textPrimary"
                            >
                                <FormattedMessage id="notificationSettings"/>
                            </Typography>

                            <FormControlLabel
                                label={intl.formatMessage({id: 'chargingEmailNotifications'})}
                                control={(
                                    <Switch
                                        sx={softSwitchSx}
                                        checked={chargingEmailsSent}
                                        disabled={isSwitchingChargingEmails}
                                        onChange={handleToggleChargingEmails}
                                    />
                                )}
                            />
                        </Box>
                    </Stack>
                )}
            />

            <DropZoneModal
                submitIsBlocked={blockSubmit}
                handleClose={handleCloseImageModal}
                open={openDropZone}
                title={uploadDialogTitle}
                handleFile={handleSetImage}
                handleSubmit={handleSubmit}
                isImage
            />
            <AMPModal
                handleClose={handleShowEditModal(false)}
                open={showEditModal}
                title={dialogTitle}
                type="md"
                fullWidth
                withoutPaddings
            >
                <EditUserForm
                    handleClose={handleShowEditModal(false)}
                    userData={data}
                    handleShowDeleteModal={handleShowDeleteModal}
                />
            </AMPModal>
            <AMPModal
                handleClose={handleShowEditNameModal(false)}
                open={showEditNameModal}
                title={dialogTitle}
                type="md"
                fullWidth
                withoutPaddings
            >
                <EditCompanyForm
                    handleClose={handleShowEditNameModal(false)}
                    userData={data}
                />
            </AMPModal>
            <AMPModal
                handleClose={handleShowDeleteModal(false)}
                open={showDeleteModal}
                type="sm"
                fullWidth
                withoutPaddings
                title=""
            >
                <Stack sx={deleteModalContentSx.root}>
                    <ErrorIcon/>
                    <Typography sx={deleteModalContentSx.text}>
                        {intl.formatMessage({id: 'cannotDeleteAccountMsg'})}
                    </Typography>
                </Stack>
                <Box sx={deleteModalContentSx.actions}>
                    <CancelButton
                        text="cancel"
                        handler={handleShowDeleteModal(false)}
                        rounding={false}
                    />
                    <SubmitButton
                        text="ok"
                        handler={handleShowDeleteModal(false)}
                        rounding={false}
                    />
                </Box>
            </AMPModal>
            <AMPModal
                handleClose={handleCloseModal}
                open={open}
                title={dialogTitle}
                type="md"
                fullWidth
            >
                <UserForm
                    handleClose={handleCloseModal}
                    businessId={data.business.id}
                />
            </AMPModal>
        </>
    );
};
